import { authConfig } from './authConfig'

// Mirrors StructuraMatrix.Auth's request limits (server/Infrastructure/RequestLimits.cs, AuthController.cs); the service re-checks every one.
export const EMAIL_MAX_LENGTH: number = 254
export const PASSWORD_MIN_LENGTH: number = 6
export const PASSWORD_MAX_LENGTH: number = 256
export const NAME_MAX_LENGTH: number = 200

const HTTP_TOO_MANY_REQUESTS: number = 429

/** rejected: the service answered with an error; rateLimited: too many requests from this IP; unreachable: no answer at all. */
export type AccountFailureKind = 'rejected' | 'rateLimited' | 'unreachable'

export class AccountApiError extends Error {
  readonly kind: AccountFailureKind

  constructor(kind: AccountFailureKind, message: string, options?: ErrorOptions) {
    super(message, options)
    this.name = 'AccountApiError'
    this.kind = kind
  }
}

export interface RegistrationInput {
  readonly email: string
  readonly password: string
  readonly name: string
  readonly companyName: string
}

type RequestBody = Readonly<Record<string, string | undefined>>

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

async function readBody(response: Response): Promise<unknown> {
  const text: string = await response.text()
  if (text.trim() === '') return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

// The service answers {error} from its controllers, but ASP.NET model binding answers ProblemDetails {title, errors}.
function describeRejection(url: string, status: number, body: unknown): string {
  if (isRecord(body) && typeof body.error === 'string' && body.error.trim() !== '') return body.error
  if (isRecord(body) && isRecord(body.errors)) return Object.values(body.errors).flat().join(' ')
  if (isRecord(body) && typeof body.title === 'string') return body.title
  return `Calling ${url} failed: HTTP ${status}${typeof body === 'string' ? ` ${body}` : ''}`
}

async function postAccount(path: string, requestBody: RequestBody): Promise<void> {
  const url: string = `${authConfig.baseUrl}/api/auth/${path}`
  let response: Response
  try {
    response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestBody) })
  } catch (error) {
    throw new AccountApiError('unreachable', `Calling ${url} failed: ${(error as Error).message}`, { cause: error })
  }
  if (response.ok) return
  if (response.status === HTTP_TOO_MANY_REQUESTS) throw new AccountApiError('rateLimited', `Calling ${url} failed: HTTP ${HTTP_TOO_MANY_REQUESTS} Too Many Requests`)
  throw new AccountApiError('rejected', describeRejection(url, response.status, await readBody(response)))
}

function optional(value: string): string | undefined {
  const trimmed: string = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/** Answers the same for a new and an existing address, so it never reveals which accounts exist. */
export function registerAccount(input: RegistrationInput): Promise<void> {
  return postAccount('register', { email: input.email.trim(), password: input.password, name: optional(input.name), companyName: optional(input.companyName), appId: authConfig.appId })
}

export function resendVerification(email: string): Promise<void> {
  return postAccount('resend-verification', { email: email.trim(), appId: authConfig.appId })
}

/** The service also returns a sign-in token here; this site keeps no session, so it is dropped. */
export function verifyEmail(token: string): Promise<void> {
  return postAccount('verify-email', { token })
}

export function requestPasswordReset(email: string): Promise<void> {
  return postAccount('forgot-password', { email: email.trim(), appId: authConfig.appId })
}

export function resetPassword(token: string, newPassword: string): Promise<void> {
  return postAccount('reset-password', { token, newPassword })
}
